import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/hooks/useAuth';
import { DataCacheProvider } from '@/hooks/useDataCache';
import { ThemeProvider } from '@/components/ThemeProvider';
import { ScriptInjector } from '@/components/ScriptInjector';
import { ChatBubbleManager } from '@/components/ChatBubbleManager';

// Pages
import LandingPage from '@/pages/LandingPage';
import SignupPage from '@/pages/SignupPage';
import TenantSelector from '@/pages/TenantSelector';
import LoginPage from '@/pages/LoginPage';
import AlumnoSignupPage from '@/pages/AlumnoSignupPage';
import RecuperarContrasena from '@/pages/RecuperarContrasena';
import RestablecerContrasena from '@/pages/RestablecerContrasena';
import AppLayout from '@/components/layouts/AppLayout';
import AlumnoPortalLayout from '@/components/layouts/AlumnoPortalLayout';
import Dashboard from '@/pages/app/Dashboard';
import Users from '@/pages/app/Users';
import Company from '@/pages/app/Company';
import Audit from '@/pages/app/Audit';
import Alumnos from '@/pages/app/Alumnos';
import Prospectos from '@/pages/app/Prospectos';
import Admin from '@/pages/app/Admin';
import Roles from '@/pages/app/Roles';
import Administration from '@/pages/app/Administration';
import Tasks from '@/pages/app/Tasks';
import TareasPendientes from '@/pages/app/TareasPendientes';
import AlumnoDashboard from '@/pages/alumno/Dashboard';
import AlumnoProfile from '@/pages/alumno/Profile';
import AlumnoKardex from '@/pages/alumno/Kardex';
import AlumnoDocuments from '@/pages/alumno/Documents';
import AlumnoHorarios from '@/pages/alumno/Horarios';
import AlumnoGrades from '@/pages/alumno/Grades';
import Planificacion from '@/pages/app/control-escolar/PlanificacionCompleta';
import RegistroAsistencia from '@/pages/app/control-escolar/RegistroAsistencia';
import KardexClases from '@/pages/app/control-escolar/KardexClases';
import ListasGrupos from '@/pages/app/control-escolar/ListasGrupos';
import Reportes from '@/pages/app/control-escolar/Reportes';
import Catalogos from '@/pages/app/catalogos/Catalogos';
import Aulas from '@/pages/app/catalogos/Aulas';
import Materias from '@/pages/app/catalogos/Materias';
import Periodos from '@/pages/app/catalogos/Periodos';
import Programas from '@/pages/app/catalogos/Programas';
import Equipamientos from '@/pages/app/catalogos/Equipamientos';
import Profesores from '@/pages/app/catalogos/Profesores';
import Productos from '@/pages/app/catalogos/Productos';
import Descuentos from '@/pages/app/catalogos/Descuentos';
import CategoriasProblemas from '@/pages/app/catalogos/CategoriasProblemas';
import FinanceDashboard from '@/pages/finance/FinanceDashboard';
import ChartOfAccounts from '@/pages/finance/ChartOfAccounts';
import NewJournalEntry from '@/pages/finance/NewJournalEntry';
import TransactionsList from '@/pages/finance/TransactionsList';
import NewInvoice from '@/pages/sales/NewInvoice';
import InvoicesList from '@/pages/sales/InvoicesList';
import TuitionGeneration from '@/pages/sales/TuitionGeneration';
import EstadoCuenta from '@/pages/sales/EstadoCuenta';
import PromotionsList from '@/pages/sales/PromotionsList';
import PaymentsList from '@/pages/treasury/PaymentsList';
import CashCuts from '@/pages/treasury/CashCuts';
import StudentCreditsList from '@/pages/treasury/StudentCreditsList';
import ProcedureTypesList from '@/pages/procedures/ProcedureTypesList';
import ProceduresList from '@/pages/procedures/ProceduresList';
import StudentProcedures from '@/pages/procedures/StudentProcedures';
import InvoicingConsole from '@/pages/cfdi/InvoicingConsole';
import SatCatalogos from '@/pages/cfdi/SatCatalogos';
import AlumnoBilling from '@/pages/alumno/Billing';
import AgendaDocente from '@/pages/docente/AgendaDocente';
import TeacherProcedures from '@/pages/procedures/TeacherProcedures';
import NuevoTicket from '@/pages/app/tickets/NuevoTicket';
import MisTickets from '@/pages/app/tickets/MisTickets';
import GestionTickets from '@/pages/app/tickets/GestionTickets';
import EvaluationPlans from '@/pages/app/evaluation/EvaluationPlans';
import Gradebook from '@/pages/app/evaluation/Gradebook';
import ExecutiveDashboard from '@/pages/app/ExecutiveDashboard';
import AperturaCierre from '@/pages/app/AperturaCierre';
import SubirComprobante from '@/pages/app/SubirComprobante';
import ValidacionComprobantes from '@/pages/app/ValidacionComprobantes';

// Create a client with optimized caching settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 60 * 1000, // 1 minute - data is fresh for 1 min before refetching
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 min
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: true, // Refetch when network reconnects
    },
  },
});

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
        <img src="/qpulse-logo.png" alt="QPulseMES" className="w-12 h-12 object-contain" />
      </div>
      <p className="text-gray-600">Cargando...</p>
    </div>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="qpulsemes-ui-theme">
        <Suspense fallback={<LoadingFallback />}>
          <Router>
            <AuthProvider>
              <DataCacheProvider>
                <ScriptInjector />
                <ChatBubbleManager />
                <div className="min-h-screen bg-background">
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/login" element={<TenantSelector />} />
                  
                  {/* Tenant login: /[nombretenant]/login/ */}
                  <Route path="/:tenant/login" element={<LoginPage />} />
                  
                  {/* Password recovery routes */}
                  <Route path="/:tenant/recuperar-contrasena" element={<RecuperarContrasena />} />
                  <Route path="/resetear-contrasena" element={<RestablecerContrasena />} />
                  
                  {/* Alumno signup: /[tenant]/alumno/registro */}
                  <Route path="/:tenant/alumno/registro" element={<AlumnoSignupPage />} />
                  
                  {/* Protected app routes (staff) */}
                  <Route path="/app" element={<AppLayout />}>
                    <Route index element={<Navigate to="/app/dashboard" replace />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="users" element={<Users />} />
                    <Route path="alumnos" element={<Alumnos />} />
                    <Route path="prospectos" element={<Prospectos />} />
                    <Route path="roles" element={<Roles />} />
                    <Route path="admin" element={<Admin />} />
                    <Route path="administration" element={<Administration />} />
                    <Route path="tasks" element={<Tasks />} />
                    <Route path="tareas-pendientes" element={<TareasPendientes />} />
                    <Route path="company" element={<Company />} />
                    <Route path="audit" element={<Audit />} />
                    <Route path="control-escolar/planificacion" element={<Planificacion />} />
                    <Route path="control-escolar/apertura-cierre" element={<AperturaCierre />} />
                    <Route path="control-escolar/registro-asistencia" element={<RegistroAsistencia />} />
                    <Route path="control-escolar/kardex" element={<KardexClases />} />
                    <Route path="control-escolar/listas" element={<ListasGrupos />} />
                    <Route path="control-escolar/reportes" element={<Reportes />} />
                    <Route path="catalogos" element={<Catalogos />} />
                    <Route path="catalogos/aulas" element={<Aulas />} />
                    <Route path="catalogos/materias" element={<Materias />} />
                    <Route path="catalogos/periodos" element={<Periodos />} />
                    <Route path="catalogos/programas" element={<Programas />} />
                    <Route path="catalogos/equipamientos" element={<Equipamientos />} />
                    <Route path="catalogos/profesores" element={<Profesores />} />
                    <Route path="catalogos/productos" element={<Productos />} />
                    <Route path="catalogos/descuentos" element={<Descuentos />} />
                    <Route path="catalogos/categorias-problemas" element={<CategoriasProblemas />} />
                    <Route path="catalogos/tramites" element={<ProcedureTypesList />} />
                    <Route path="control-escolar/tramites-academicos" element={<ProceduresList />} />
                    <Route path="finance" element={<FinanceDashboard />} />
                    <Route path="finance/chart-of-accounts" element={<ChartOfAccounts />} />
                    <Route path="finance/new-entry" element={<NewJournalEntry />} />
                    <Route path="finance/transactions" element={<TransactionsList />} />
                    <Route path="sales/invoices" element={<InvoicesList />} />
                    <Route path="sales/invoices/new" element={<NewInvoice />} />
                    <Route path="sales/tuitions" element={<TuitionGeneration />} />
                    <Route path="sales/estado-cuenta" element={<EstadoCuenta />} />
                    <Route path="treasury/payments" element={<PaymentsList />} />
                    <Route path="treasury/corte-caja" element={<CashCuts />} />
                    <Route path="treasury/creditos" element={<StudentCreditsList />} />
                    <Route path="finance/invoicing" element={<InvoicingConsole />} />
                    <Route path="cfdi/sat-catalogos" element={<SatCatalogos />} />
                    <Route path="docente/tramites" element={<TeacherProcedures />} />
                    <Route path="docente/agenda" element={<AgendaDocente />} />
                    <Route path="tickets/nuevo" element={<NuevoTicket />} />
                    <Route path="tickets/mis-tickets" element={<MisTickets />} />
                    <Route path="tickets/gestion" element={<GestionTickets />} />
                    <Route path="evaluation/plans" element={<EvaluationPlans />} />
                    <Route path="evaluation/gradebook" element={<Gradebook />} />
                    <Route path="executive-dashboard" element={<ExecutiveDashboard />} />
                    <Route path="treasury/comprobantes" element={<ValidacionComprobantes />} />
                    <Route path="treasury/subir-comprobante" element={<SubirComprobante />} />
                    <Route path="sales/promotions" element={<PromotionsList />} />
                  </Route>
                  
                  {/* Protected alumno portal routes */}
                  <Route path="/alumno" element={<AlumnoPortalLayout />}>
                    <Route index element={<Navigate to="/alumno/dashboard" replace />} />
                    <Route path="dashboard" element={<AlumnoDashboard />} />
                    <Route path="perfil" element={<AlumnoProfile />} />
                    <Route path="kardex" element={<AlumnoKardex />} />
                    <Route path="documentos" element={<AlumnoDocuments />} />
                    <Route path="tramites" element={<StudentProcedures />} />
                    <Route path="horarios" element={<AlumnoHorarios />} />
                    <Route path="calificaciones" element={<AlumnoGrades />} />
                    <Route path="facturacion" element={<AlumnoBilling />} />
                    <Route path="comprobante-pago" element={<SubirComprobante />} />
                  </Route>
                  
                  {/* Catch all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </div>
                <Toaster />
              </DataCacheProvider>
            </AuthProvider>
          </Router>
        </Suspense>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;